import { DownloadOutlined, EditOutlined, LockOutlined, SaveOutlined, SearchOutlined, SendOutlined, ArrowLeftOutlined, ReloadOutlined, DeleteOutlined } from '@ant-design/icons';
import { Button, Col, Form, Input, InputNumber, Row, Space, Tabs, Select, Tag, DatePicker } from 'antd';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ContentHeader } from '../../components';
import ModalTextForm from '../../components/modal/ModalTextForm';
import { formatVND, validateMessages } from '../../helpers/common';
import { ChiTietLuongHoanThanhNhiemVuItem, LuongHoanThanhNhiemVuInfo } from '../../models/luong/LuongHoanThanhNhiemVuModel';
import TableChiTietLuongHoanThanhNhiemVu from '../../modules/salary/TableChiTietLuongHoanThanhNhiemVu';
import { capNhatQuyLuongHoanThanhNhiemVu, chiTietLuongHoanThanhNhiemVu, duyetLuongHoanThanhNhiemVu, layThongTinLuongHoanThanhNhiemVu, luuDieuChinhChiTietLuongHoanThanhNhiemVu, luuLuongHoanThanhNhiemVu, taiDuLieuLuongHoanThanhNhiemVu, xoaBangLuongHoanThanhNhiemVu, layDsLuongHoanThanhNhiemVuTheoNam, capNhatThangLayDuLieuHoanThanhNhiemVu, xuatExcelLuongHoanThanhNhiemVu } from '../../services/luong';
import { hideLoadingScreen, showLoadingScreen } from '../../store/reducers/loading';
import { Links } from '../../helpers/Links';
import dayjs, { Dayjs } from 'dayjs';

const generateMonths = () => {
    const months = [];
    const now = dayjs();
    const startDate = now.subtract(3, 'year').startOf('year');
    const endDate = now.add(1, 'year').endOf('year');
    
    let current = startDate;
    while (current.isBefore(endDate) || current.isSame(endDate)) {
        months.push(current.format('MM/YYYY'));
        current = current.add(1, 'month');
    }
    return months.reverse();
};

const ChiTietLuongHoanThanhNhiemVu = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();

    const [form] = Form.useForm();

    const [id, setId] = useState<number>(0);
    const [info, setInfo] = useState<LuongHoanThanhNhiemVuInfo | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [tuKhoa, setTuKhoa] = useState<string>('');

    const [data, setData] = useState<ChiTietLuongHoanThanhNhiemVuItem[]>([]);
    const [rawData, setRawData] = useState<ChiTietLuongHoanThanhNhiemVuItem[]>([]);

    const [isEdit, setIsEdit] = useState<boolean>(false);
    const [changedRowIds, setChangedRowIds] = useState<Set<number>>(new Set());
    const [reload, setReload] = useState<boolean>(false);
    const [isShowModalDuyet, setIsShowModalDuyet] = useState<boolean>(false);
    const [isShowModalCapNhat, setIsShowModalCapNhat] = useState<boolean>(false);
    const [isShowModalXoa, setIsShowModalXoa] = useState<boolean>(false);
    const [ghiChu, setGhiChu] = useState<string>('');
    const [dsKyLuongDaTao, setDsKyLuongDaTao] = useState<any[]>([]);
    const [selectYear, setSelectYear] = useState(dayjs().year());

    const setChiTiet = (items: ChiTietLuongHoanThanhNhiemVuItem[]) => {
        setRawData(items);
        setData(items);
    };

    const layThongTin = async (salaryId: number) => {
        dispatch(showLoadingScreen());
        setLoading(true);
        try {
            const rs = await layThongTinLuongHoanThanhNhiemVu(salaryId);
            setInfo(rs);
            setGhiChu(rs?.ghiChu ?? '');

            const ct = await chiTietLuongHoanThanhNhiemVu(salaryId);
            setChiTiet(ct ?? []);
        } catch (err: any) {
            toast.error(err?.toString().replace('Error: ', ''));
        }
        setLoading(false);
        dispatch(hideLoadingScreen());
    };

    const handleTinh = async () => {
        dispatch(showLoadingScreen());
        setLoading(true);
        try {
            const ct = await taiDuLieuLuongHoanThanhNhiemVu(id);
            setChiTiet(ct ?? []);
            setChangedRowIds(new Set());
            setIsEdit(false);
            toast.success('Tính lương hoàn thành nhiệm vụ thành công');
        } catch (err: any) {
            toast.error(err?.toString().replace('Error: ', ''));
        }
        setLoading(false);
        dispatch(hideLoadingScreen());
    };

    const handleLuu = async () => {
        dispatch(showLoadingScreen());
        try {
            await luuLuongHoanThanhNhiemVu({ id, ghiChu, chiTiet: rawData });
            toast.success('Lưu lương hoàn thành nhiệm vụ thành công');
            setReload(true);
        } catch (err: any) {
            toast.error(err?.toString().replace('Error: ', ''));
        }
        dispatch(hideLoadingScreen());
    };

    const handleDuyet = async () => {
        dispatch(showLoadingScreen());
        try {
            await duyetLuongHoanThanhNhiemVu(id);
            toast.success('Xác nhận lương hoàn thành nhiệm vụ thành công');
            setIsShowModalDuyet(false);
            setReload(true);
        } catch (err: any) {
            toast.error(err?.toString().replace('Error: ', ''));
        }
        dispatch(hideLoadingScreen());
    };

    const handleXoa = async () => {
        dispatch(showLoadingScreen());
        try {
            await xoaBangLuongHoanThanhNhiemVu(id);
            toast.success('Xóa bảng lương hoàn thành nhiệm vụ thành công');
            navigate(Links.Luong.DanhSachLuongHoanThanhNhiemVu);
        } catch (err: any) {
            toast.error(err?.toString().replace('Error: ', ''));
        }
        dispatch(hideLoadingScreen());
    };

    const onChangeItem = (index: number, field: string, value: any) => {
        if (field !== 'soTienDuocNhan') return;

        const updated = [...data];
        const row = { ...updated[index], soTienDuocNhan: Number(value) || 0 };
        updated[index] = row;
        setData(updated);

        // Chênh lệch quỹ lương sẽ tự cập nhật vì được tính lại từ rawData ở phần render.
        const full = [...rawData];
        const idx = full.findIndex((x) => x.id === row.id);
        if (idx >= 0) {
            full[idx] = row;
            setRawData(full);
        }

        setChangedRowIds((prev) => {
            const ns = new Set(prev);
            ns.add(row.id);
            return ns;
        });
    };

    const handleLuuDieuChinh = async () => {
        if (changedRowIds.size === 0) {
            toast.warning('Không có dữ liệu thay đổi!');
            return;
        }

        dispatch(showLoadingScreen());
        try {
            const items = rawData
                .filter((x) => changedRowIds.has(x.id))
                .map((x) => ({
                    id: x.id,
                    ketQuaHTNV: x.ketQuaHTNV,
                    heSoHTNV: x.heSoHTNV,
                    soTienDuocNhan: x.soTienDuocNhan,
                    ghiChu: x.ghiChu
                }));

            await luuDieuChinhChiTietLuongHoanThanhNhiemVu({ items });

            toast.success(`Lưu điều chỉnh thành công (${items.length} dòng)`);
            setChangedRowIds(new Set());
            setIsEdit(false);
            setReload(true);
        } catch (err: any) {
            toast.error(err?.toString().replace('Error: ', ''));
        }
        dispatch(hideLoadingScreen());
    };

    const normalizeMonthsList = (strOrArr?: string | string[]): string[] => {
        if (!strOrArr) return [];
        const list = Array.isArray(strOrArr) ? strOrArr : strOrArr.split(',');
        return list.map((m) => {
            const trimmed = m.trim();
            const parts = trimmed.split('/');
            if (parts.length === 2) {
                return `${parts[0].padStart(2, '0')}/${parts[1]}`;
            }
            return trimmed;
        }).filter(Boolean);
    };

    const onFinishCapNhatQuyLuong = async (values: any) => {
        dispatch(showLoadingScreen());
        try {
            const request = {
                id: id,
                quyLuong: values?.quyLuong,
                tenKyLuong: values?.tenKyLuong
            };
            await capNhatQuyLuongHoanThanhNhiemVu(request);

            if (info?.loai === 1 || values?.thangLayDuLieu !== undefined) {
                const requestThang = {
                    id: id,
                    thangLayDuLieu: normalizeMonthsList(values?.thangLayDuLieu).join(',')
                };
                await capNhatThangLayDuLieuHoanThanhNhiemVu(requestThang);
            }

            toast.success('Cập nhật dữ liệu tham chiếu thành công!');
            setIsShowModalCapNhat(false);

            // Refresh dữ liệu
            await layThongTin(id);

            // Tính lại lương
            await handleTinh();
        } catch (err: any) {
            toast.error(err?.toString().replace('Error: ', ''));
        }
        dispatch(hideLoadingScreen());
    };

    const search = () => {
        const kw = (tuKhoa || '').toLowerCase();

        setData(
            rawData.filter(
                (x) =>
                    !kw ||
                    (x.hoTen ?? '').toLowerCase().includes(kw) ||
                    (x.ma ?? '').toLowerCase().includes(kw) ||
                    (x.tenDonVi ?? '').toLowerCase().includes(kw)
            )
        );
    };

    const onMoModalCapNhatQuyLuong = async () => {
        setSelectYear(dayjs().year());
        // Set giá trị quỹ lương hiện tại vào form
        form.setFieldsValue({
            quyLuong: info?.quyLuong,
            thangLayDuLieu: normalizeMonthsList(info?.thangLayDuLieu),
            tenKyLuong: info?.tenKyLuong
        });

        setIsShowModalCapNhat(true);
    };

    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const salaryId = urlParams.get('id');

        if (salaryId) {
            setId(Number(salaryId));
            layThongTin(Number(salaryId));
        }
    }, [location.search]);

    useEffect(() => {
        if (reload && id > 0) {
            setReload(false);
            layThongTin(id);
        }
    }, [reload]);

    const isDaDuyet = (info?.trangThai ?? 0) === 1;

    const coDuLieuDaLuu = rawData.length > 0 && rawData.some((x) => x.id > 0);
    const coTheDieuChinh = !isDaDuyet && coDuLieuDaLuu;

    const tongNhanSu = rawData.length;
    const tongHeSo = Math.round(rawData.reduce((s, x) => s + (x.heSoHTNV ?? 0), 0) * 100) / 100;
    const tongTienDuocNhan = rawData.reduce((s, x) => s + (x.soTienDuocNhan ?? 0), 0);
    const quyLuong = info?.quyLuong ?? 0;
    const chenhLechQuyLuong = Math.round(quyLuong - tongTienDuocNhan);

    const handleExportExcel = async () => {
        if (id <= 0) return;
        dispatch(showLoadingScreen());
        try {
            const res = await xuatExcelLuongHoanThanhNhiemVu(id);
            const fileName = (info?.tenKyLuong || 'BangLuongHoanThanhNhiemVu').replace(/[\/\\?%*:|"<>]/g, '_') + '.xlsx';
            const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = fileName;
            link.click();
            window.URL.revokeObjectURL(link.href);
            toast.success('Xuất file Excel thành công!');
        } catch (err: any) {
            toast.error(err?.toString().replace('Error: ', ''));
        }
        dispatch(hideLoadingScreen());
    };

    return (
        <section>
            <ContentHeader title={info?.tenKyLuong ?? 'Lương hoàn thành nhiệm vụ'} />

            <Row className="form-group" gutter={8}>
                <Col xs={22} sm={14} md={8}>
                    <Input
                        prefix={<SearchOutlined />}
                        placeholder="Nhập tên, mã nhân viên, đơn vị"
                        value={tuKhoa}
                        onChange={(e) => setTuKhoa(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') search();
                        }}
                    />
                </Col>

                <Col xs={2} sm={2} md={0}>
                    <Button type="primary" icon={<SearchOutlined />} onClick={search} />
                </Col>

                <Col xs={0} sm={0} md={4}>
                    <Button type="primary" icon={<SearchOutlined />} onClick={search}>
                        Tìm kiếm
                    </Button>
                </Col>

                <Col xs={24} sm={24} md={12}>
                    <Space className="float-right">
                        <Button type="default" icon={<ArrowLeftOutlined />} onClick={() => { navigate(Links.Luong.DanhSachLuongHoanThanhNhiemVu) } }>
                                Quay lại
                        </Button>
                        {!isDaDuyet && (
                            <Button
                                danger
                                type="primary"
                                icon={<DeleteOutlined />}
                                onClick={() => setIsShowModalXoa(true)}
                            >
                                Xóa dữ liệu
                            </Button>
                        )}
                        {!isDaDuyet && (
                            <Button type="primary" icon={<ReloadOutlined />} onClick={onMoModalCapNhatQuyLuong}>
                                {info?.loai === 1 ? 'Điều chỉnh thông tin' : 'Điều chỉnh quỹ lương'}
                            </Button>
                        )}
                        {!isDaDuyet && (
                            <Button type="primary" icon={<DownloadOutlined />} onClick={handleTinh}>
                                Tính lương
                            </Button>
                        )}

                        {!isDaDuyet && (
                            <Button
                                type="primary"
                                icon={<SendOutlined />}
                                onClick={() => setIsShowModalDuyet(true)}
                            >
                                Xác nhận
                            </Button>
                        )}

                        {!isDaDuyet && (
                            <Button
                                className="hrm-btn hrm-btn-warning"
                                icon={<SaveOutlined />}
                                onClick={handleLuu}
                            >
                                Lưu
                            </Button>
                        )}
                        <Button className="hrm-btn hrm-btn-success" icon={<DownloadOutlined />} onClick={handleExportExcel}>
                            Xuất Excel
                        </Button>
                    </Space>
                </Col>
            </Row>

            <Row gutter={8} className="form-group">
                <Col xs={24} sm={8} md={3}>
                    <div className="card card-primary" style={{ padding: '10px' }}>
                        <div className="text-center" style={{ fontSize: '12px' }}>
                            Quỹ lương
                        </div>
                        <div className="text-bold text-center">{formatVND(quyLuong)}</div>
                    </div>
                </Col>

                <Col xs={24} sm={8} md={3}>
                    <div className="card card-primary" style={{ padding: '10px' }}>
                        <div className="text-center" style={{ fontSize: '12px' }}>
                            Tổng hệ số HTNV
                        </div>
                        <div className="text-bold text-center">{tongHeSo.toFixed(2)}</div>
                    </div>
                </Col>

                <Col xs={24} sm={8} md={3}>
                    <div className="card card-primary" style={{ padding: '10px' }}>
                        <div className="text-center" style={{ fontSize: '12px' }}>
                            Tổng tiền được nhận
                        </div>
                        <div className="text-bold text-center">{formatVND(tongTienDuocNhan)}</div>
                    </div>
                </Col>

                <Col xs={24} sm={8} md={3}>
                    <div className="card card-primary" style={{ padding: '10px' }}>
                        <div className="text-center" style={{ fontSize: '12px' }}>
                            Số nhân sự
                        </div>
                        <div className="text-bold text-center">{tongNhanSu}</div>
                    </div>
                </Col>

                <Col xs={24} sm={16} md={8}>
                    <div className="card card-primary" style={{ padding: '10px' }}>
                        <div className="text-center" style={{ fontSize: '12px' }}>
                            Chênh lệch quỹ lương - {chenhLechQuyLuong === 0 ? 'Đã phân bổ hết quỹ' : chenhLechQuyLuong > 0 ? 'Còn dư quỹ lương' : 'Vượt quỹ lương'}
                        </div>
                        <div className="text-bold text-center">
                            <span
                                style={{
                                    color: chenhLechQuyLuong === 0 ? '#52c41a' : chenhLechQuyLuong > 0 ? '#faad14' : '#ff4d4f',
                                }}
                            >
                                {formatVND(chenhLechQuyLuong)}
                            </span>
                        </div>
                    </div>
                </Col>

                <Col xs={24} sm={16} md={4}>
                    <div className="card card-primary" style={{ padding: '10px' }}>
                        <div className="text-center" style={{ fontSize: '12px' }}>
                            Trạng thái
                        </div>
                        <div className="text-bold text-center">
                            {isDaDuyet ? (
                                <span style={{ color: '#52c41a' }}>Đã xác nhận</span>
                            ) : (
                                <span style={{ color: '#faad14' }}>Chưa xác nhận</span>
                            )}
                        </div>
                    </div>
                </Col>
            </Row>

            <Row gutter={8} className="form-group">
                <Col span={24}>
                    <div className="card card-primary" style={{ padding: '15px' }}>
                        <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '10px' }}>Ghi chú</div>
                        {!isDaDuyet ? (
                            <Input.TextArea
                                rows={3}
                                placeholder="Nhập ghi chú..."
                                value={ghiChu}
                                onChange={(e) => setGhiChu(e.target.value)}
                            />
                        ) : (
                            <div style={{ whiteSpace: 'pre-wrap', minHeight: '40px', background: '#f5f5f5', padding: '10px', borderRadius: '4px' }}>
                                {ghiChu || 'Không có ghi chú'}
                            </div>
                        )}
                    </div>
                </Col>
            </Row>

            <Row>
                <Col span={24}>
                    <div className="card-outline">
                        <Tabs
                            tabBarExtraContent={
                                <Space>
                                    {coTheDieuChinh && (
                                        <Button
                                            style={{ width: '140px' }}
                                            type="default"
                                            onClick={() => {
                                                if (isEdit && changedRowIds.size > 0) {
                                                    layThongTin(id);
                                                    setChangedRowIds(new Set());
                                                }

                                                setIsEdit(!isEdit);
                                            }}
                                            icon={
                                                isEdit ? (
                                                    <LockOutlined style={{ color: 'red' }} />
                                                ) : (
                                                    <EditOutlined style={{ color: '#007bff' }} />
                                                )
                                            }
                                        >
                                            {isEdit ? 'Khóa' : 'Điều chỉnh'}
                                        </Button>
                                    )}

                                    {isEdit && changedRowIds.size > 0 && (
                                        <Button
                                            className="hrm-btn hrm-btn-warning"
                                            icon={<SaveOutlined />}
                                            onClick={handleLuuDieuChinh}
                                        >
                                            Lưu điều chỉnh ({changedRowIds.size})
                                        </Button>
                                    )}
                                </Space>
                            }
                            defaultActiveKey="1"
                            items={[
                                {
                                    key: '1',
                                    label: 'Bảng lương hoàn thành nhiệm vụ',
                                    children: (
                                        <TableChiTietLuongHoanThanhNhiemVu
                                            loading={loading}
                                            dataSource={data}
                                            isEdit={isEdit}
                                            onChangeItem={onChangeItem}
                                            loai={info?.loai}
                                            thangLayDuLieu={info?.thangLayDuLieu}
                                        />
                                    ),
                                },
                            ]}
                        />
                    </div>
                </Col>
            </Row>

            {isShowModalCapNhat && (
                <ModalTextForm
                    title={info?.loai === 1 ? "Cập nhật thông tin bổ sung/quyết toán" : "Cập nhật dữ liệu quỹ lương HTNV"}
                    hideFooter
                    show={isShowModalCapNhat}
                    handleClose={() => setIsShowModalCapNhat(false)}
                >
                    <Form
                        layout="vertical"
                        validateMessages={validateMessages}
                        onFinish={onFinishCapNhatQuyLuong}
                        form={form}
                    >
                        <Row gutter={8}>
                            <Col span={24}>
                                <Form.Item label="Tên kỳ lương" name="tenKyLuong" rules={[{ required: info?.loai === 1, message: 'Vui lòng nhập tên kỳ lương' }]}>
                                    <Input placeholder={info?.loai === 1 ? "Nhập tên kỳ bổ sung/quyết toán..." : "Nhập tên kỳ lương (tuỳ chọn)"} />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={8}>
                            <Col span={info?.loai === 1 ? 12 : 24}>
                                <Form.Item label="Quỹ lương hoàn thành nhiệm vụ" name="quyLuong">
                                    <InputNumber
                                        min={0}
                                        step={1000}
                                        style={{ width: '100%' }}
                                        formatter={(val) => `${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                        parser={(val) => Number(`${val}`.replace(/,/g, '')) || 0}
                                    />
                                </Form.Item>
                            </Col>
                             {info?.loai === 1 && (
                                <Col span={12}>
                                    <Form.Item rules={[{ required: true, message: 'Vui lòng chọn các tháng!' }]} label="Chọn tháng lấy dữ liệu" name="thangLayDuLieu">
                                        <Select
                                            mode="multiple"
                                            placeholder="Chọn các tháng"
                                            dropdownStyle={{ minWidth: 220 }}
                                            tagRender={(props) => {
                                                const { label, closable, onClose } = props;
                                                return (
                                                    <Tag closable={closable} onClose={onClose} style={{ marginRight: 3 }}>
                                                        {label}
                                                    </Tag>
                                                );
                                            }}
                                            dropdownRender={() => {
                                                const value = normalizeMonthsList(form.getFieldValue('thangLayDuLieu') || []);
                                                return (
                                                    <div style={{ padding: 8, background: '#fff' }} onMouseDown={(e) => e.preventDefault()}>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, borderBottom: '1px solid #f0f0f0', paddingBottom: 8 }}>
                                                            <Button size="small" type="text" onClick={() => setSelectYear(selectYear - 1)}>&lt;</Button>
                                                            <strong>Năm {selectYear}</strong>
                                                            <Button size="small" type="text" onClick={() => setSelectYear(selectYear + 1)}>&gt;</Button>
                                                        </div>
                                                        <Row gutter={[4, 4]}>
                                                            {Array.from({ length: 12 }, (_, i) => {
                                                                const mNum = i + 1;
                                                                const mStr = `${mNum < 10 ? '0' + mNum : mNum}/${selectYear}`;
                                                                const isSelected = value.includes(mStr);
                                                                return (
                                                                    <Col span={8} key={mStr}>
                                                                        <Button
                                                                            size="small"
                                                                            type={isSelected ? "primary" : "default"}
                                                                            style={{ width: '100%', fontSize: '12px', padding: '0 4px' }}
                                                                            onClick={() => {
                                                                                const newValue = isSelected
                                                                                    ? value.filter((x: string) => x !== mStr)
                                                                                    : normalizeMonthsList([...value, mStr]).sort((a, b) => {
                                                                                        const [ma, ya] = a.split('/').map(Number);
                                                                                        const [mb, yb] = b.split('/').map(Number);
                                                                                        if (ya !== yb) return (ya || 0) - (yb || 0);
                                                                                        return (ma || 0) - (mb || 0);
                                                                                    });
                                                                                form.setFieldValue('thangLayDuLieu', newValue);
                                                                            }}
                                                                        >
                                                                            Thg {mNum}
                                                                        </Button>
                                                                    </Col>
                                                                );
                                                            })}
                                                        </Row>
                                                    </div>
                                                );
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                            )}
                        </Row>
                        <Form.Item>
                            <Button
                                type="primary"
                                className="float-right"
                                htmlType="submit"
                                icon={<SaveOutlined />}
                            >
                                Cập nhật
                            </Button>
                        </Form.Item>
                    </Form>
                </ModalTextForm>
            )}

            <ModalTextForm
                show={isShowModalDuyet}
                title="Xác nhận lương hoàn thành nhiệm vụ"
                handleClose={() => setIsShowModalDuyet(false)}
                handleSave={handleDuyet}
            >
                Bạn có chắc muốn xác nhận lương hoàn thành nhiệm vụ{' '}
                <b>{info?.tenKyLuong ?? ''}</b> không?
            </ModalTextForm>

            <ModalTextForm
                show={isShowModalXoa}
                title="Xóa bảng lương hoàn thành nhiệm vụ"
                handleClose={() => setIsShowModalXoa(false)}
                handleSave={handleXoa}
            >
                Bạn có chắc chắn muốn xóa bảng lương hoàn thành nhiệm vụ <b>{info?.tenKyLuong ?? ''}</b>? Hành động này sẽ xóa toàn bộ dữ liệu chi tiết của bảng lương.
            </ModalTextForm>
        </section>
    );
};

export default ChiTietLuongHoanThanhNhiemVu;